import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import SendMoneyIcon from 'components/atoms/SVG/SendMoneyIcon.component';
import PaymentAmount from 'components/molecules/Fields/PaymentAmount/PaymentAmount.component';
import ModalHeading from 'components/molecules/ModalHeading';
import { joiResolver } from '@hookform/resolvers/joi';
import useInvalidateTransferDependencies from 'hooks/Bank/useInvalidateTransferDependencies';
import useMakeTransfer from 'hooks/Bank/useMakeTransfer';
import Joi from 'joi';
import { motion } from 'motion/react';
import type { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CurrenciesEnum } from 'types/global.types';
import type { Connector, Transaction } from 'types/transactions.types';
import { StandingOrderFrequency } from 'types/transactions.types';
import { WEBAPP_BANKING_PAYMENTTRANSFER_INTERNALTRANSFER_CLICKEDCONFIRMTRANSFER } from 'utils/amplitude/events/bank.amplitude';
import { fadeTransition } from 'utils/constants';
import formatMoney from 'utils/formatMoney';
import {
  type TransferReviewData,
  TransferFieldNames,
  TransferReasons,
  TransferTypes,
} from '../types';
import SimilarTransactionAlert from '../../SimilarTransactionAlert.component';
import useWalletTopUpBankAccount from '../../useWalletTopUpBankAccount';
import { transformFormDataToReviewData } from '../utils/dataTransforms';
import { round } from 'utils/math';
import {
  MIN_TRANSFER_AMOUNT,
  transferAmountSchema,
} from '../constants/externalTransferSchema';

interface InternalTransferFormProps {
  sourceAccount: Connector;
  destinationAccount: Connector;
  initialAmount?: number;
  onTransferSuccess?: (transaction?: Transaction) => void;
  onReview?: (data: TransferReviewData) => void;
  showReviewStep?: boolean;
  withRedirection?: boolean;
  redirectionParams?: Record<string, any>;
  successRedirectUri?: string;
}

const InternalTransferComponent: FC<InternalTransferFormProps> = ({
  sourceAccount,
  destinationAccount,
  initialAmount,
  onTransferSuccess,
  onReview,
  showReviewStep = true,
  withRedirection = false,
  redirectionParams,
  successRedirectUri,
}) => {
  const { t } = useTranslation();
  const { walletTopUpBankAccountContent, openWalletTopUpBankAccountModal } =
    useWalletTopUpBankAccount();

  const invalidateTransferDeps = useInvalidateTransferDependencies();

  const { mutate, isLoading } = useMakeTransfer({
    withRedirection,
    redirectionParams,
    successRedirectUri,
    onTransferSuccess: () => {
      onTransferSuccess?.();
      invalidateTransferDeps();
    },
  });

  const formAPI = useForm({
    mode: 'onChange',
    defaultValues: { [TransferFieldNames.amount]: initialAmount || 0 },
    resolver: joiResolver(
      Joi.object({
        [TransferFieldNames.amount]: transferAmountSchema(
          sourceAccount?.balance.amount,
        ),
      }),
    ),
  });

  const transferAmountValue = formAPI.watch(TransferFieldNames.amount);
  const roundedTransferAmount = round(transferAmountValue, 100);

  const amountError = formAPI.formState.errors[TransferFieldNames.amount];
  const isAmountTouched =
    formAPI.formState.touchedFields[TransferFieldNames.amount];

  const amountExceedsSourceAccountBalance =
    roundedTransferAmount > (sourceAccount?.balance.amount || 0);
  const isAmountInvalid =
    amountExceedsSourceAccountBalance ||
    transferAmountValue < MIN_TRANSFER_AMOUNT ||
    !!amountError;

  // Only show red styling if field has been touched and is invalid
  const shouldShowErrorStyling = isAmountTouched && isAmountInvalid;

  const handleInternalTransferSubmit = () => {
    if (!sourceAccount || !destinationAccount || isAmountInvalid) return;

    // If we should show review step, prepare review data and call onReview
    if (showReviewStep && onReview) {
      // Create form data object for internal transfer
      const formData = {
        amount: roundedTransferAmount,
        period: StandingOrderFrequency.OneTime,
        firstExecutionDate: new Date(),
        toConnectorId: destinationAccount._id, // Include destination account ID
        // Let the transfer type be determined by the actual form data
        // Don't hardcode instant or communication_type
      };

      // Use the transformFormDataToReviewData function to properly create review data
      const reviewData = transformFormDataToReviewData(
        formData,
        mutate,
        isLoading,
        sourceAccount._id,
        destinationAccount,
      );

      onReview(reviewData);
    } else {
      // Direct submission (for backward compatibility)
      mutate({
        fromConnectorId: sourceAccount._id,
        toConnectorId: destinationAccount._id,
        amount: { value: roundedTransferAmount, currency: 'EUR' },
        reason: TransferReasons.transfer,
        type: TransferTypes.internal,
      });
    }
  };

  return (
    <motion.div
      {...fadeTransition}
      key="internal-review"
      className="flex items-center flex-col w-full px-6"
    >
      <ModalHeading
        Icon={SendMoneyIcon}
        title={t('accountable_banking.internal_transfer.title')}
        subtitle={t('accountable_banking.internal_transfer.subtitle')}
      />

      {walletTopUpBankAccountContent}

      <motion.div {...fadeTransition} className="w-full my-6">
        <FormProvider {...formAPI}>
          <form
            className="mb-6 text-center"
            onSubmit={formAPI.handleSubmit(handleInternalTransferSubmit)}
          >
            <FieldConnector name={TransferFieldNames.amount}>
              {({ field }) => (
                <PaymentAmount
                  {...field}
                  format={{ asMoney: true, maximumFractions: 2 }}
                  onChange={(value) => field.onChange(round(value ?? 0, 100))}
                  immedialtyCommitOnChange
                  className={classNames(
                    '!text-4xl !font-bold !text-primary !border-0 !text-center',
                    {
                      '!text-red-600':
                        shouldShowErrorStyling ||
                        (roundedTransferAmount === 0 && isAmountTouched),
                    },
                  )}
                  placeholder={t('amountSign')}
                />
              )}
            </FieldConnector>

            <p className="text-center text-primary-700 mt-3 mb-6">
              {t('accountable_banking.internal_transfer.available_amount', {
                bankName: sourceAccount?.title,
                amount: formatMoney(
                  sourceAccount?.balance?.amount as number,
                  sourceAccount?.balance?.currency,
                ),
              })}
            </p>

            {amountExceedsSourceAccountBalance ? (
              <>
                <Button
                  className="mt-6 w-full"
                  onClick={() =>
                    openWalletTopUpBankAccountModal({
                      connector: sourceAccount,
                    })
                  }
                >
                  {t('accountable_banking.top_up.cta', {
                    bankAccountName: sourceAccount?.title,
                  })}
                </Button>

                {(sourceAccount?.balance.amount || 0) > 0 && (
                  <Button
                    className="mt-3 w-full"
                    structure="secondary"
                    onClick={() => {
                      formAPI.setValue(
                        TransferFieldNames.amount,
                        sourceAccount?.balance.amount || 0,
                      );
                      // Trigger validation after setting the value
                      formAPI.trigger(TransferFieldNames.amount);
                    }}
                  >
                    {t('accountable_banking.set_aside.cta')}{' '}
                    {formatMoney(sourceAccount?.balance.amount || 0)}
                  </Button>
                )}
              </>
            ) : (
              <div className="space-y-4">
                <SimilarTransactionAlert
                  amount={roundedTransferAmount}
                  currency={CurrenciesEnum.EUR}
                  toIBAN={destinationAccount?.bankAccountReference?.IBAN || ''}
                  fromIBAN={sourceAccount?.bankAccountReference?.IBAN || ''}
                  onConfirm={handleInternalTransferSubmit}
                  isLoading={isLoading}
                />

                <Button
                  onClick={handleInternalTransferSubmit}
                  loading={isLoading}
                  className="w-full"
                  disabled={
                    !roundedTransferAmount || roundedTransferAmount <= 0
                  }
                  tracingEvent={
                    WEBAPP_BANKING_PAYMENTTRANSFER_INTERNALTRANSFER_CLICKEDCONFIRMTRANSFER
                  }
                >
                  {showReviewStep
                    ? t('accountable_banking.review_transfer.title')
                    : t('confirm')}
                </Button>
              </div>
            )}
          </form>
        </FormProvider>
      </motion.div>
    </motion.div>
  );
};

export default InternalTransferComponent;
